import { cn } from "@/lib/utils"

interface SectionEyebrowProps {
  children: React.ReactNode
  /** Default tone is crimson (the editorial accent). Use "ink-soft" for a
   *  calm variant in dense lists where the crimson would fight the
   *  surrounding content, or "white-soft" only on rare media/dark overlays. */
  tone?: "crimson" | "ink-soft" | "white-soft"
  /** Default size matches the body of marketing section labels. Use "lg"
   *  for hero/intro eyebrows and "sm" for dense cards/sidebars. */
  size?: "sm" | "md" | "lg"
  className?: string
  as?: "p" | "span" | "div" | "h2" | "h3"
}

const TONE = {
  crimson: "text-[var(--color-vermilion)]",
  "ink-soft": "text-[var(--color-ink-soft)]",
  "white-soft": "text-white/65",
} as const

const SIZE = {
  // Bumped up so labels stay scan-able. Old sm (0.7rem / 0.18em tracking)
  // was hitting the UI_PREFERENCES warning about "tiny tracked-out
  // uppercase." Tracking pulls back just a touch so wide labels still
  // read as eyebrows but legibility wins on small screens.
  sm: "text-[0.76rem] tracking-[0.14em]",
  md: "text-[0.82rem] tracking-[0.14em]",
  lg: "text-[0.94rem] tracking-[0.14em]",
} as const

/**
 * Canonical section-level eyebrow label. Mono, uppercase, tracked-out,
 * crimson by default. Use anywhere a small label sits above an h1/h2/h3
 * to mark the "edition" or "department" of the section.
 *
 * If you find yourself reaching for a different style, talk to the design
 * habit instead: this component should cover ~all marketing eyebrows
 * site-wide. Drift here is what the user explicitly asked us to stop.
 */
export function SectionEyebrow({
  children,
  tone = "crimson",
  size = "md",
  className,
  as = "p",
}: SectionEyebrowProps) {
  const Tag = as
  return (
    <Tag
      className={cn(
        "font-mono font-bold uppercase leading-snug",
        SIZE[size],
        TONE[tone],
        className,
      )}
    >
      {children}
    </Tag>
  )
}
